import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import React from "react";
import {
  AssessmentResponse,
  AssessmentStep,
  LearnerSubmitAssessmentResultsMutation,
  StartAssessment,
  StartAssessmentMutation,
  StartAssessmentMutationVariables,
} from "apps/user/learner/api/types";
import { useAuth } from "providers/auth";
import { ensureFreshSession } from "providers/auth-tokens";
import { authenticationFailed } from "providers/apollo-client";
import appInfo from "utility/app-info";
import { ApolloError, useMutation } from "@apollo/client";
import {
  LEARNER_SUBMIT_ASSESSMENT_RESULTS,
  REPORT_ASSESSMENT_EVENT,
  SAVE_AND_EXIT_ASSESSMENT,
  SAVE_ASSESSMENT_PROGRESS,
} from "utility/graphQl/mutation";
import { logAnalyticsEvent } from "utility/FirebaseAnalytics";
import {
  START_ASSESSMENT,
  START_ASSESSMENT_OVER,
} from "utility/graphQl/mutation";
import { logError } from "utility/Function";
import useFetchHomeData from "apps/user/learner/hooks/use-fetch-home-data";
import useAssessmentReportEnabled from "apps/user/learner/hooks/use-assessment-report-enabled";
import useFeatureFlags, {
  FEATURE_FLAGS,
} from "apps/user/learner/hooks/use-feature-flags";
import {
  useDraftSaveQueue,
  whenDraftSavesSettle,
} from "providers/assessment-draft-save-queue";
import {
  getCurrentTimestamp,
  calculateElapsedTimeInSeconds,
} from "utility/time";
import {
  firstVisibleStepIndexFrom,
  isHiddenInFlowStep,
} from "apps/user/learner/pages/assessment/companySectionStep";

export enum AssessmentEvent {
  StartAssessment = "START_ASSESSMENT",
  AssessmentStepViewed = "ASSESSMENT_STEP_VIEWED",
  AssessmentQuestionAnswered = "ASSESSMENT_QUESTION_ANSWERED",
  AssessmentQuestionSkipped = "ASSESSMENT_QUESTION_SKIPPED",
  AssessmentCompleted = "ASSESSMENT_COMPLETED",
  ExitAssessment = "EXIT_ASSESSMENT",
  AssessmentSubmissionError = "ASSESSMENT_SUBMISSION_ERROR",
}

export type OnNextParams = Pick<AssessmentResponse, "text" | "score"> & {
  optionId?: string | null;
};

export type CompanyQuestionResponse = Pick<
  AssessmentResponse,
  "text" | "score"
> & {
  stepId: string;
  optionId?: string | null;
};

type ReportAssessmentEventParams = {
  eventType: AssessmentEvent;
  params?: object;
  // Callers emitting a batch pass an explicit millisecond per event. Events
  // are reported fire-and-forget, so a batch would otherwise share a
  // timestamp and collide on the user_events uniqueness constraint
  // (user_id, type_id, platform, user_reported_at), failing the mutation.
  atMs?: number;
};

interface AssessmentContextInterface {
  assessmentVersion: string;
  steps: AssessmentStep[];
  step: AssessmentStep;
  currentstepIndex: number;
  percentageProgress: number;
  onNext: (response?: OnNextParams) => void;
  onNextCompanyQuestions: (responses: CompanyQuestionResponse[]) => void;
  onBack: () => void;
  onSkip: () => void;
  onSubmit: () => void;
  getStepResponse: (stepId: string) => OnNextParams | null;
  hasAnyResponses: boolean;
  saveAndResumeSupported: boolean;
  saveProgressExpiresAt: string | null;
  autoSaveEnabled: boolean;
  onSaveAndExit: () => Promise<void>;
  isSavingProgress: boolean;
  assessmentError: ApolloError | undefined;
  assessmentLoading: boolean;
  isLoadingResults: boolean;
  isErrorResults: boolean;
  resultsData: undefined | null | LearnerSubmitAssessmentResultsMutation;
  reportAssessmentEvent: ({
    eventType,
    params,
    atMs,
  }: ReportAssessmentEventParams) => Promise<void>;
}

const AssessmentContext = createContext<AssessmentContextInterface>({
  assessmentVersion: "",
  resultsData: undefined,
  steps: [],
  step: { id: "0", __typename: "AssessmentWelcomeStep" },
  currentstepIndex: 0,
  percentageProgress: 0,
  onNext: () => null,
  onNextCompanyQuestions: () => null,
  onBack: () => null,
  onSkip: () => null,
  onSubmit: () => null,
  getStepResponse: () => null,
  hasAnyResponses: false,
  saveAndResumeSupported: false,
  saveProgressExpiresAt: null,
  autoSaveEnabled: false,
  onSaveAndExit: () => Promise.resolve(),
  isSavingProgress: false,
  assessmentError: undefined,
  assessmentLoading: true,
  isLoadingResults: false,
  isErrorResults: false,
  reportAssessmentEvent: () => new Promise(() => null),
});

type AssessmentResponseInput = {
  version: string;
  responses: (Pick<AssessmentResponse, "text" | "score"> & {
    step_id: string;
    option_id?: string | null;
  })[];
};

type AssessmentDraft = {
  responses: AssessmentResponseInput["responses"];
  stepId: string | null;
};

// `text` is required by the schema and is empty for a plain select, whose
// answer the option id carries instead. Sending both keeps a resumed attempt
// able to restore the exact option.
const toResponsesVariable = (responses: AssessmentResponseInput["responses"]) =>
  responses.map((response) => ({
    stepId: response.step_id,
    text: response.text ?? "",
    score: response.score,
    ...(response.option_id ? { optionId: response.option_id } : {}),
  }));

interface AssessmentProviderProps {
  children: React.ReactNode;
}

// How long an exit waits for an earlier one still out before going ahead, so a
// hung request cannot hold every later exit on "Saving". The earlier exit
// still sends the newest answers again whenever it does settle.
const EARLIER_EXIT_WAIT_MS = 10_000;

const settledOrTimedOut = (settled: Promise<void>, ms: number) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    settled.then(() => {
      clearTimeout(timer);
      resolve();
    });
  });

const INDEX_TAB_PATH = "/user/woneindex";
const REPORT_PATH = "/user/woneindex/report";

const AssessmentProvider: React.FC<AssessmentProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const uuidRef = useRef(window.crypto.randomUUID());
  const urlParams = new URLSearchParams(window.location.search);
  const learner = useAuth();
  const [
    submitResults,
    { loading: isLoadingResults, error: isErrorResults, data: resultsData },
  ] = useMutation(LEARNER_SUBMIT_ASSESSMENT_RESULTS);

  const [reportAssessmentEventMutation] = useMutation(REPORT_ASSESSMENT_EVENT);
  const [saveAndExitAssessment, { loading: isSavingAndExiting }] = useMutation(
    SAVE_AND_EXIT_ASSESSMENT
  );
  const [isDrainingForExit, setIsDrainingForExit] = useState(false);
  const exitsSettled = useRef<Promise<void>>(Promise.resolve());
  const isSavingProgress = isSavingAndExiting || isDrainingForExit;
  const [saveAssessmentProgress] = useMutation(SAVE_ASSESSMENT_PROGRESS);
  const startTime = useRef(new Date().getTime());

  const { refetchHome } = useFetchHomeData();
  const { enabled: reportEnabled } = useAssessmentReportEnabled();
  const { isEnabled } = useFeatureFlags();

  if (!learner.user) {
    navigate("/signin", { replace: true });
  }

  const [currentstepIndex, setCurrentstepIndex] = useState<number>(0);
  const shouldSkipCompanysteps = useRef<boolean>(
    urlParams.get("cf4") === "skip_company_questions"
  );
  const [assessmentVersion, setAssessmentVersion] = useState<string | null>(
    urlParams.get("version")
  );
  const [assessment, setAssessment] = useState<StartAssessment | undefined>();
  // Only an attempt the server can resume is worth saving as it goes.
  const autoSaveEnabled =
    isEnabled(FEATURE_FLAGS.ASSESSMENT_AUTO_SAVE) &&
    Boolean(assessment?.saveAndResumeSupported);
  const [assessmentResponse, setAssessmentResponse] =
    useState<AssessmentResponseInput | null>(null);

  const handleStarted = (data: StartAssessmentMutation) => {
    if (!assessment) {
      const started = data.startAssessment;
      const version = started.version;
      setAssessment(started as StartAssessment);

      setAssessmentVersion(version);

      // A resumed attempt comes back with each previously saved answer
      // hanging off its own step, so the flow can be rehydrated without a
      // second round-trip. Seeding here (rather than on first render)
      // keeps back-navigation and the final submit reading the same
      // `responses` array they would hold had the learner never left.
      const savedResponses = (started.steps ?? []).flatMap((step) => {
        if (step?.__typename !== "AssessmentQuestionStep" || !step.response) {
          return [];
        }

        const text = step.response.text ?? "";
        // Progress is stored as text/score, but the option cards highlight
        // by option id — resolve the saved answer back to the option it
        // came from so a resumed question shows the learner's pick already
        // selected. Picker answers (duration/count) hold a raw value
        // and no option; those restore from `text` instead.
        const savedOptionId = step.response.optionId;
        const option =
          step.input.__typename === "SelectInput" && savedOptionId
            ? step.input.options.find((o) => o.id === savedOptionId)
            : undefined;

        return [
          {
            step_id: step.id,
            text,
            score: step.response.score ?? 0,
            ...(option?.id ? { option_id: option.id } : {}),
          },
        ];
      });

      if (!assessmentResponse) {
        setAssessmentResponse({
          version: version,
          responses: savedResponses,
        });
      }

      const resumeIndex = started.resumeStepId
        ? (started.steps ?? []).findIndex(
            (step) => step?.id === started.resumeStepId
          )
        : -1;

      if (resumeIndex > 0) {
        setCurrentstepIndex(resumeIndex);
      }
    }
  };

  const [startAssessment, { loading: isStarting, error: startError }] =
    useMutation<StartAssessmentMutation, StartAssessmentMutationVariables>(
      START_ASSESSMENT,
      {
        variables: { appInfo, version: assessmentVersion },
        onCompleted: handleStarted,
      }
    );
  const [
    startAssessmentOver,
    { loading: isStartingOver, error: startOverError },
  ] = useMutation<StartAssessmentMutation, StartAssessmentMutationVariables>(
    START_ASSESSMENT_OVER,
    {
      variables: { appInfo, version: assessmentVersion },
      onCompleted: (data) => {
        handleStarted(data);
        // Home still holds the discarded attempt as resumable until it
        // hears otherwise.
        refetchHome().catch((error) => {
          logError("Error refetching home after starting over", error);
        });
      },
    }
  );
  const assessmentLoading = isStarting || isStartingOver;
  const assessmentError = startError ?? startOverError;

  // The learner chose "Start over" on the way here. Read once, for the
  // navigation that mounted this provider, and cleared from the history entry
  // before the start goes out, so a reload or re-render cannot discard the
  // new attempt's answers as well.
  const location = useLocation();
  const [startOverRequested] = useState(
    () => (location.state as { startOver?: boolean } | null)?.startOver === true
  );
  const startRequested = useRef(false);

  // Read when the start goes out rather than as a dependency, so the flag
  // resolving afterwards cannot start the attempt a second time. The flag
  // alone: whether the attempt can be resumed is only known once it starts.
  const autoSaveFlagAtStart = useRef(false);
  autoSaveFlagAtStart.current = isEnabled(FEATURE_FLAGS.ASSESSMENT_AUTO_SAVE);

  useEffect(() => {
    if (startRequested.current) return;
    startRequested.current = true;
    if (startOverRequested) {
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
        },
        { replace: true, state: null }
      );
    }
    const start = startOverRequested ? startAssessmentOver : startAssessment;
    // Saves still out from an earlier visit would otherwise land after this
    // start has read the draft back. With auto-save off, or not yet known,
    // the start goes out at once.
    const earlierSaves = autoSaveFlagAtStart.current
      ? whenDraftSavesSettle()
      : null;
    if (earlierSaves) earlierSaves.then(() => start());
    else start();
  }, [
    startOverRequested,
    startAssessment,
    startAssessmentOver,
    navigate,
    location.pathname,
    location.search,
    location.hash,
  ]);

  const draftSaveQueue = useDraftSaveQueue<AssessmentDraft>(
    ({ responses, stepId }, { keepalive }) => {
      const variables = {
        appInfo,
        responses: toResponsesVariable(responses),
        stepId,
      };
      // A flush runs from pagehide or visibilitychange. Apollo reaches the
      // network through microtasks only, which the browser runs before the
      // page is torn down, and keepalive lets the request outlive the page.
      // A hidden tab's save also gets Apollo's token refresh and retry.
      return saveAssessmentProgress({
        variables,
        ...(keepalive && { context: { fetchOptions: { keepalive: true } } }),
      });
    },
    (error) => logError("Error auto-saving assessment progress", error),
    autoSaveEnabled
  );
  // Counts answers recorded by the handlers below, so seeding a resumed
  // attempt's saved answers does not echo them straight back to the server.
  // It is state rather than a ref so it commits in the same render as the
  // answer it counts.
  const [answersRecorded, setAnswersRecorded] = useState(0);
  const answersSaved = useRef(0);

  const userID = localStorage.getItem("userID");
  const companyName = localStorage.getItem("companyName") || "NA";

  const steps = assessment?.steps as AssessmentStep[];

  const step = steps && steps.length && steps[currentstepIndex];

  const percentageProgress: number = steps?.length
    ? (steps.slice(0, currentstepIndex).length / (steps.length - 1)) * 100
    : 0;

  const reportAssessmentEvent = async ({
    eventType,
    params,
    atMs,
  }: ReportAssessmentEventParams) => {
    // Never send this fire-and-forget mutation anonymously: an expired
    // access token is rotated first, a dead session bounces to sign-in,
    // and a transient refresh failure just drops the event — analytics
    // aren't worth signing the user out over.
    const session = await ensureFreshSession();
    if (session.kind !== "ok" || !session.token) {
      if (session.kind === "terminal") {
        authenticationFailed();
      } else {
        logError(
          `Skipped assessment event ${eventType}: no authenticated session`,
          session.kind
        );
      }
      return;
    }
    await reportAssessmentEventMutation({
      variables: {
        eventType,
        params: JSON.stringify(params),
        appInfo,
        at: atMs !== undefined ? atMs.toString() : getCurrentTimestamp(),
        groupUuid: uuidRef.current,
      },
    }).catch((error) =>
      logError(`Error reporting assessment event ${eventType}`, error)
    );
  };

  // The completion itself is reported server-side by
  // learnerSubmitAssessmentResults; this is the analytics stream that has no
  // server-side equivalent.
  const logAssessmentCompleted = async () => {
    await logAnalyticsEvent("assessment_completed", {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      assessment_version: assessmentVersion,
      step_id: step.id,
      step_type: step.__typename,
      time_taken: calculateElapsedTimeInSeconds(startTime.current),
    });
  };

  const logSubmitResultsError = async (error: string) => {
    const params = {
      assessment_version: assessmentVersion,
      step_id: step.id,
      step_type: step.__typename,
      error,
    };

    await logAnalyticsEvent("assessment_submission_error", {
      user_id: userID,
      user_id_wone: userID,
      company_name: companyName,
      ...params,
    });

    reportAssessmentEvent({
      eventType: AssessmentEvent.AssessmentSubmissionError,
      params,
    });
  };

  const handleSubmit = async () => {
    // The submission carries every answer and ends the attempt, so a draft
    // save landing after it would find no attempt to save to.
    if (autoSaveEnabled) {
      await draftSaveQueue.drain();
      await whenDraftSavesSettle();
      draftSaveQueue.close();
    }
    submitResults({
      variables: {
        appInfo,
        input: {
          responseJson: JSON.stringify(assessmentResponse),
        },
      },
    })
      .then(() => {
        // The mutation resolves only after its transaction commits, so the
        // score is durable by the time we get here. refetchHome is what keeps
        // the results screen from painting the previous score, so it stays.
        // The fixed wait this used to sit behind bought nothing and is gone
        // with the flag that gated it (WONE-2526).
        return refetchHome()
          .catch((error) => {
            logError("Error refetching home", error);
          })
          .finally(() => {
            // Tell the modal provider this arrival was a redirect out of the
            // assessment, so it does not cover the results. The report reads
            // the same state on its way out to the Index tab.
            navigate(reportEnabled ? REPORT_PATH : INDEX_TAB_PATH, {
              replace: true,
              state: { suppressBiometricsModal: true },
            });
          });
      })
      .catch(async (error) => {
        logError("Error Submiting WONE Index Assessment Results", error);
        await logSubmitResultsError(error.toString());
        navigate("/user/woneindex/assessment/error", { replace: true });
      });
  };

  const isFeedbackStep = (step: AssessmentStep) =>
    step.__typename === "AssessmentQuestionStep" &&
    step.input.label === "feedback";
  const isCompanyFeedbackStep = (step: AssessmentStep) =>
    step.__typename === "AssessmentQuestionStep" &&
    step.input.label === "co-feedback";

  const handleNext = async (response?: OnNextParams) => {
    if (step.__typename === "AssessmentQuestionStep") {
      if (step.input.__typename === "SelectInput") {
        // Trust the caller's score when it computed one (every uplift
        // input type — cards, duration/count pickers — already resolves
        // score against the authored option, independent of what `text`
        // holds). Only fall back to matching `text` against an option
        // label for callers that don't pass a score (the legacy carousel).
        const score =
          response?.score ??
          parseInt(
            step.input.options.filter(
              (option) => option.label === response.text
            )[0]?.value
          ) ??
          0;

        setAnswersRecorded((count) => count + 1);
        setAssessmentResponse((prevState) => ({
          ...prevState,
          responses: prevState.responses
            .filter((r) => r.step_id !== step.id)
            .concat([
              {
                step_id: step.id,
                text: response.text,
                score: score,
                ...(response.optionId ? { option_id: response.optionId } : {}),
              },
            ]),
        }));
      } else if (step.input.__typename === "TextInput") {
        setAnswersRecorded((count) => count + 1);
        setAssessmentResponse((prevState) => ({
          ...prevState,
          responses: prevState.responses
            .filter((r) => r.step_id !== step.id)
            .concat([
              {
                step_id: step.id,
                text: response.text || "",
                score: response.score || 0,
              },
            ]),
        }));
      }
    }
    if (isCompanyFeedbackStep(step) && response?.score === 0) {
      shouldSkipCompanysteps.current = true;
      setCurrentstepIndex(steps.length - 1);
      return;
    }

    if (isFeedbackStep(step) && shouldSkipCompanysteps.current) {
      setCurrentstepIndex(steps.length - 1);
      return;
    }

    if (step.__typename === "AssessmentEndStep") {
      await logAssessmentCompleted();
      navigate("/user/woneindex/assessment/interstitial-animation");
    }

    if (!steps.length) {
      throw new Error("No steps available");
    } else if (step.__typename !== "AssessmentEndStep") {
      setCurrentstepIndex((currentIndex) => currentIndex + 1);
    }
  };

  // The company-questions modal answers several steps at once, outside the
  // normal one-step-at-a-time onNext flow, then completes the assessment —
  // mirroring mobile's handleAssessmentCompleted(extraResponses) merge.
  const handleNextCompanyQuestions = async (
    responses: CompanyQuestionResponse[]
  ) => {
    const answeredStepIds = new Set(responses.map((r) => r.stepId));

    setAnswersRecorded((count) => count + 1);
    setAssessmentResponse((prevState) => ({
      ...prevState,
      responses: prevState.responses
        .filter((r) => !answeredStepIds.has(r.step_id))
        .concat(
          responses.map((r) => ({
            step_id: r.stepId,
            text: r.text,
            score: r.score,
            ...(r.optionId ? { option_id: r.optionId } : {}),
          }))
        ),
    }));

    await logAssessmentCompleted();
    navigate("/user/woneindex/assessment/interstitial-animation");
  };

  const handleBack = () => {
    if (
      step.__typename === "AssessmentEndStep" &&
      shouldSkipCompanysteps.current
    ) {
      shouldSkipCompanysteps.current = false;
      let feedbackStep = steps.findIndex(
        (step) =>
          step.__typename === "AssessmentQuestionStep" &&
          step.input.label === "co-feedback"
      );

      if (feedbackStep === -1) {
        feedbackStep = steps.findIndex(
          (step) =>
            step.__typename === "AssessmentQuestionStep" &&
            step.input.label === "feedback"
        );
      }

      setCurrentstepIndex(feedbackStep);
    } else if (currentstepIndex > 0) {
      // The commercial-questions section intro and its questions are never
      // shown as their own steps (index.tsx auto-skips them forward) — the
      // company-questions modal off the end screen collects them instead.
      // Stepping back one index at a time would land directly inside that
      // hidden block with nothing to skip it back out again, since the
      // auto-skip effects only fire forward. Walk back past the whole block
      // in one go so `step` always resolves to a real, visible step.
      let previousIndex = currentstepIndex - 1;
      while (previousIndex > 0 && isHiddenInFlowStep(steps[previousIndex])) {
        previousIndex -= 1;
      }
      setCurrentstepIndex(previousIndex);
    }
  };

  // Runs after the render that records the answer, which also moved the
  // learner on. That move can land on a step the page is about to skip, so
  // the draft records the next visible one, as save-and-exit would.
  const resumeStepId = steps?.length
    ? steps[firstVisibleStepIndexFrom(steps, currentstepIndex)].id
    : null;
  useEffect(() => {
    // Left pending while auto-save is off, so an answer given before the flag
    // resolves still goes out once it resolves on.
    if (!autoSaveEnabled || answersSaved.current === answersRecorded) return;
    answersSaved.current = answersRecorded;
    draftSaveQueue.save({
      responses: assessmentResponse.responses,
      stepId: resumeStepId,
    });
  }, [
    answersRecorded,
    assessmentResponse,
    resumeStepId,
    autoSaveEnabled,
    draftSaveQueue,
  ]);

  const getStepResponse = (stepId: string): OnNextParams | null => {
    const recorded = assessmentResponse?.responses.find(
      (r) => r.step_id === stepId
    );
    if (!recorded) return null;

    return {
      text: recorded.text,
      score: recorded.score,
      ...(recorded.option_id ? { optionId: recorded.option_id } : {}),
    };
  };

  // The step on screen is the authoritative resume position — it differs from
  // the furthest answered step whenever the learner skipped or blanked a
  // question, and only the client knows which one they were looking at.
  // A failed save deliberately rejects rather than being caught here: the
  // caller keeps the learner in the exit modal with an error instead of
  // navigating away and dropping the answers. Swallowing it here would report
  // success and lose them.
  //
  // The exit writes the draft outside the auto-save queue, with the answers
  // as they stand when it begins. A learner can walk away from it and carry
  // on, so it may land after newer auto-saves: the queue counts those unsaved
  // while it is out and sends them again the moment it settles, either way,
  // since a rejected call may still have been applied.
  const handleSaveAndExit = async () => {
    const endExternalWrite = autoSaveEnabled
      ? draftSaveQueue.beginExternalWrite()
      : () => undefined;
    const earlierExitsSettled = exitsSettled.current;
    let markSettled: () => void = () => undefined;
    exitsSettled.current = new Promise((resolve) => {
      markSettled = resolve;
    });

    try {
      if (autoSaveEnabled) {
        setIsDrainingForExit(true);
        try {
          // An earlier exit still out carries older answers, so this one goes
          // after it, and after the auto-saves it may have set off, unless it
          // hangs.
          await settledOrTimedOut(earlierExitsSettled, EARLIER_EXIT_WAIT_MS);
          await draftSaveQueue.drain();
        } finally {
          setIsDrainingForExit(false);
        }
      }
      try {
        await saveAndExitAssessment({
          variables: {
            appInfo,
            responses: toResponsesVariable(assessmentResponse?.responses ?? []),
            stepId: step?.id ?? null,
          },
        });
      } catch (error) {
        // With auto-save on, a confirmed auto-save already holds every
        // answer, so this failing loses only the exit record and the learner
        // can leave.
        if (!autoSaveEnabled || !draftSaveQueue.isSaved()) throw error;
        logError(
          "Error recording the assessment exit after auto-saving",
          error
        );
      }
    } finally {
      endExternalWrite();
      markSettled();
    }

    await refetchHome().catch((error) => {
      logError("Error refetching home after saving assessment progress", error);
    });
  };

  const handleSkip = () => {
    if (isFeedbackStep(step) && shouldSkipCompanysteps.current) {
      setCurrentstepIndex(steps.length - 1);
      return;
    }

    if (!steps.length) {
      throw new Error("No steps available");
    } else if (step.__typename !== "AssessmentEndStep") {
      setCurrentstepIndex((currentIndex) => currentIndex + 1);
    }
  };

  return (
    <AssessmentContext.Provider
      value={{
        resultsData,
        assessmentVersion,
        steps,
        currentstepIndex,
        step,
        percentageProgress,
        onNext: handleNext,
        onNextCompanyQuestions: handleNextCompanyQuestions,
        onBack: handleBack,
        onSkip: handleSkip,
        getStepResponse,
        hasAnyResponses: (assessmentResponse?.responses.length ?? 0) > 0,
        saveAndResumeSupported: Boolean(assessment?.saveAndResumeSupported),
        saveProgressExpiresAt: assessment?.progressExpiresAt ?? null,
        autoSaveEnabled,
        onSaveAndExit: handleSaveAndExit,
        isSavingProgress,
        assessmentError,
        assessmentLoading,
        isLoadingResults,
        onSubmit: handleSubmit,
        isErrorResults: Boolean(isErrorResults),
        reportAssessmentEvent,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export const useAssessment = () => useContext(AssessmentContext);

export default AssessmentProvider;
